import React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { showModal } from 'modal.react.js';
import { any, groupBy, pipe, values } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useLocalStorage } from 'usehooks-ts';

import area from 'components/shared/GlobalModalArea/area';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import { EOrganizedBy } from 'detailsv2/structure';
import { showSnackbar } from 'hooks/useSnackbar';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { mergeScheduleItems, switchScheduleItemTypeAction } from 'store/itineraryDetails';
import { getIsInitialized, getItineraryScheduleSelector } from 'store/itineraryDetails/selectors';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { userSelector } from 'store/user/selectors';
import { isSample } from 'utils/itineraries/functions';
import { isDefined } from 'utils/ramda';

import { useTryToRemindCheckPrimaryImagesForDetailsTab } from '../functions';
import { SuccessResult } from '../MergeItineraryModal/index.types';
import { MergeItineraryModal } from '../MergeItineraryModal/MergeItineraryModal';
import { addScheduleActions, mergeSchedulesAction } from './constants';
import { IScheduleCreateAction, Schedule, WarningsIgnoredMap, WarningsType } from './types';

export const useUnsavedChangesWatcher = () => {
  const COMPONENT_STATES = useUnsavedChangesWatcher.COMPONENT_STATES;
  const dispatch = useDispatch();
  const schedule = useSelector(getItineraryScheduleSelector);
  const isInitialized = useSelector(getIsInitialized);
  const componentStateRef = useRef(COMPONENT_STATES.IDLE);
  const componentState = componentStateRef.current;
  const scheduleCopyRef = useRef(null);
  const scheduleCopy = scheduleCopyRef.current;
  const { setInitialAreaPropertyPageIds, tryToRemindCheckPrimaryImages } =
    useTryToRemindCheckPrimaryImagesForDetailsTab();

  useEffect(() => {
    if (isInitialized && componentState === COMPONENT_STATES.IDLE) {
      scheduleCopyRef.current = schedule;
      componentStateRef.current = COMPONENT_STATES.UNCHANGED;
      setInitialAreaPropertyPageIds();
    }
  }, [isInitialized]);

  useEffect(() => {
    if (componentState === COMPONENT_STATES.UNCHANGED && scheduleCopy !== schedule) {
      scheduleCopyRef.current = schedule;
      componentStateRef.current = COMPONENT_STATES.CHANGED;
      dispatch(setAppHasChangesAction(true));
    }
  }, [schedule]);

  const onBeforeSave = () => {
    componentStateRef.current = COMPONENT_STATES.PAUSED;
    dispatch(setAppHasChangesAction(false));
  };

  const onAfterSave = () => {
    tryToRemindCheckPrimaryImages();
    setInitialAreaPropertyPageIds();

    scheduleCopyRef.current = schedule;
    componentStateRef.current = COMPONENT_STATES.UNCHANGED;
  };

  const onAfterNext = useCallback(() => {
    tryToRemindCheckPrimaryImages();
  }, [tryToRemindCheckPrimaryImages]);

  return { onBeforeSave, onAfterSave, onAfterNext };
};
useUnsavedChangesWatcher.COMPONENT_STATES = {
  IDLE: 'IDLE',
  UNCHANGED: 'UNCHANGED',
  CHANGED: 'CHANGED',
  PAUSED: 'PAUSED',
};

export function useAddScheduleMenuActions({
  scheduleItemId,
  onAfterTypeSelected,
}: {
  scheduleItemId: number;
  onAfterTypeSelected: () => void;
}) {
  const dispatch = useDispatch();
  const { libType } = useParams<{ libType?: string }>();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const schedule = useSelector(getItineraryScheduleSelector);
  const isOrganizedByDay = schedule.type === EOrganizedBy.Day;
  const isOrganizedByDate = schedule.type === EOrganizedBy.Date;
  const isSampleProposal = isSample(libType);

  const createSchedule = React.useCallback(
    (action: IScheduleCreateAction) => {
      dispatch(switchScheduleItemTypeAction({ id: scheduleItemId, type: action.type }));
      if (onAfterTypeSelected) onAfterTypeSelected();
    },
    [scheduleItemId, onAfterTypeSelected],
  );

  const mergeItinerary = React.useCallback(
    async (result: SuccessResult) => {
      const schedules = result.data?.schedules;
      if (schedules && schedules.length > 0) {
        await dispatch(
          mergeScheduleItems({
            scheduleItemId,
            schedules,
            referenceDate: result.refereceDate,
            referenceDay: result.referenceDay,
            targetOrganizedBy: schedule.type,
            sourceOrganizedBy: result.data!.scheduleType,
          }),
        );
      } else {
        throw new Error('No schedules found');
      }
    },
    [scheduleItemId, schedule.type],
  );

  const handleMergeItinerary = React.useCallback(async () => {
    showModal(
      area,
      <MergeItineraryModal
        isSample={isSampleProposal}
        itineraryId={itinerary.id}
        isShowDatePicker={isOrganizedByDate}
        isShowDayPicker={isOrganizedByDay}
        onFormSuccess={mergeItinerary}
      />,
    );
  }, [isSampleProposal, itinerary.id, isOrganizedByDate, isOrganizedByDay, mergeItinerary]);

  const actions = React.useMemo(() => {
    return [
      // create action
      ...addScheduleActions.map((action) => {
        return {
          ...action,
          onClick: () => createSchedule(action),
        };
      }),
      // merge action
      {
        ...mergeSchedulesAction,
        onClick: handleMergeItinerary,
      },
    ];
  }, [createSchedule, handleMergeItinerary]);

  return actions;
}

export function validateContentPresence(schedule: Schedule) {
  return schedule.items
    .filter((item) => !item._destroy)
    .some(
      (event) =>
        isDefined(event.details?.contentState?.entityMap) || isDefined(event.accommodation?.contentState?.entityMap),
    );
}

export function validateNoDuplicatedContentOnConsecutiveRows(schedule: Schedule) {
  const hasDuplicatedContentOnConsecutiveRows = schedule.items
    .filter((item) => !item._destroy)
    .some((event, index: number) => {
      if (index === 0) {
        return false;
      }

      const previousEvent = schedule.items[index - 1];

      const eventDetailsIds = values(event.details?.contentState?.entityMap || {}).map((entity) => entity.data.id);
      const previousEventDetailsIds = values(previousEvent.details?.contentState?.entityMap || {}).map(
        (entity) => entity.data.id,
      );
      const eventAccommodationIds = values(event.accommodation?.contentState?.entityMap || {}).map(
        (entity) => entity.data.id,
      );
      const previousEventAccommodationIds = values(previousEvent.accommodation?.contentState?.entityMap || {}).map(
        (entity) => entity.data.id,
      );

      const hasDuplicatedDetails = eventDetailsIds.some((id) => previousEventDetailsIds.includes(id));
      const hasDuplicatedAccommodation =
        event.type !== 'activity' && eventAccommodationIds.some((id) => previousEventAccommodationIds.includes(id));

      return hasDuplicatedDetails || hasDuplicatedAccommodation;
    });

  return !hasDuplicatedContentOnConsecutiveRows;
}

export function validateNoDuplicatedContentOnSameRow(schedule: Schedule) {
  const hasDuplicatedContentOnSameRow = schedule.items
    .filter((item) => !item._destroy)
    .some((event) => {
      const checkForDuplicates = pipe(
        values,
        groupBy((entity: { data: { id: string } }) => entity.data.id),
        // @ts-expect-error
        values,
        // @ts-expect-error
        any((entities) => entities.length > 1),
      );

      const hasDuplicatedDetails = checkForDuplicates(event.details?.contentState?.entityMap || {});
      const hasDuplicatedAccommodation =
        event.type !== 'activity' && checkForDuplicates(event.accommodation?.contentState?.entityMap || {});

      return hasDuplicatedDetails || hasDuplicatedAccommodation;
    });

  return !hasDuplicatedContentOnSameRow;
}

const IGNORED_WARNING_KEY = 'IGNORED_WARNING_ORIGINAL_KEY_V_1.0';
export const useIgnoreWarning = () => {
  const currentUserId = useSelector(userSelector).id;
  const itinerary = useSelector(itinerariesItemDataSelector);
  const [ignoredWarningMap, setIgnoredWarningMap] = useLocalStorage<WarningsIgnoredMap>(IGNORED_WARNING_KEY, {});

  const checkIsWarningIgnored = (warningType: WarningsType) => {
    try {
      if (!currentUserId) throw new Error('Current user id is not defined');
      const userIgnoredProposals = ignoredWarningMap[currentUserId];
      const proposalIgnoredTypes = userIgnoredProposals?.[itinerary.id];
      if (!proposalIgnoredTypes) return false;

      if (!proposalIgnoredTypes.includes(warningType)) return false;

      return true;
    } catch (e) {
      console.error(e);
      showSnackbar({
        uniqueId: 'checkIsWarningIgnored',
        text: 'Something went wrong with warning ignoring. Please try again.',
        type: SNACKBAR_SKIN.ERROR,
      });
      return false;
    }
  };

  const ignoreWarning = (warningType: WarningsType) => {
    try {
      if (!currentUserId) throw new Error('Current user id is not defined');

      const userIgnoredProposals = ignoredWarningMap[currentUserId] || {};
      const proposalIgnoredTypes = userIgnoredProposals?.[itinerary.id] || [];

      proposalIgnoredTypes.push(warningType);
      userIgnoredProposals[itinerary.id] = proposalIgnoredTypes;
      ignoredWarningMap[currentUserId] = userIgnoredProposals;
      setIgnoredWarningMap(ignoredWarningMap);
    } catch (e) {
      console.error(e);
      showSnackbar({
        uniqueId: 'ignoreWarning',
        text: 'Something went wrong with warning ignoring. Please try again.',
        type: SNACKBAR_SKIN.ERROR,
      });
    }
  };

  return [checkIsWarningIgnored, ignoreWarning] as const;
};
